import { useState } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ArrowLeft, Ticket } from 'lucide-react'

interface CouponUse {
  id: number
  user_name: string
  discount_applied: number
  created_at: string
}

interface CouponFull {
  id: number
  code: string
  description: string | null
  discount_type: string
  discount_value: number
  min_purchase: number
  max_uses: number | null
  uses_count: number
  max_uses_per_user: number
  active: boolean
  expires_at: string | null
  uses: CouponUse[]
}

interface Props {
  coupon: CouponFull
}

export default function CouponsEdit({ coupon }: Props) {
  const [form, setForm] = useState({
    code: coupon.code,
    description: coupon.description || '',
    discount_type: coupon.discount_type,
    discount_value: coupon.discount_value.toString(),
    min_purchase: coupon.min_purchase > 0 ? coupon.min_purchase.toString() : '',
    max_uses: coupon.max_uses?.toString() || '',
    max_uses_per_user: coupon.max_uses_per_user.toString(),
    active: coupon.active,
    expires_at: coupon.expires_at || ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch(`/coupons/${coupon.id}`, { coupon: form })
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => router.visit('/coupons')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Ticket className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Editar Cupom</h1>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Dados do Cupom</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="font-mono uppercase"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Desconto</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                      <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="discount_value">
                    Valor {form.discount_type === 'percentage' ? '(%)' : '(R$)'} *
                  </Label>
                  <Input
                    id="discount_value"
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={form.discount_type === 'percentage' ? '100' : undefined}
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_purchase">Compra Mínima (R$)</Label>
                  <Input
                    id="min_purchase"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.min_purchase}
                    onChange={(e) => setForm({ ...form, min_purchase: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="expires_at">Validade</Label>
                  <Input
                    id="expires_at"
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max_uses">Limite Total de Usos</Label>
                  <Input
                    id="max_uses"
                    type="number"
                    min="1"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    placeholder="Ilimitado"
                  />
                </div>
                <div>
                  <Label htmlFor="max_uses_per_user">Usos por Aluno</Label>
                  <Input
                    id="max_uses_per_user"
                    type="number"
                    min="1"
                    value={form.max_uses_per_user}
                    onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="active">Cupom Ativo</Label>
                  <p className="text-sm text-muted-foreground">Desative para pausar o uso sem excluir</p>
                </div>
                <Switch
                  id="active"
                  checked={form.active}
                  onCheckedChange={(checked) => setForm({ ...form, active: checked })}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => router.visit('/coupons')}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Histórico de Usos */}
        {coupon.uses && coupon.uses.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histórico de Usos ({coupon.uses.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Desconto Aplicado</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupon.uses.map((use) => (
                    <TableRow key={use.id}>
                      <TableCell className="font-medium">{use.user_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">R$ {use.discount_applied.toFixed(2)}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{use.created_at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}
